import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { finalizeAllianceTaskPeriod, type AllianceTaskPeriodClient } from '../../common/allianceApi'
import { ALLIANCE_TASK_APS_TARGETS, ALLIANCE_TASK_TITLE_STYLE } from '../../common/allianceTaskOptionsClient'
import { alert } from '../Alert/Alert'
import { TaskFooter, TaskModalShell, TaskOptionCard } from './allianceTaskModalKit'

interface AllianceApsTargetModalProps {
  open: boolean
  address: string
  period: AllianceTaskPeriodClient
  onBack: () => void
  onFinalized: () => void
}

const BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal'
const MODAL_BG = `${BASE}/modal-bg.webp`
const CARD_BG = `${BASE}/card-bg.webp`
const LOCK_BTN = `${BASE}/lock-btn.webp`
const APS_ICON = `${BASE}/aps-icon.webp`
const CINZEL = '"Cinzel", "Times New Roman", serif'

/**
 * APS growth-target modal — shown after the leader picks the "Increase APS" official task.
 * The leader must commit to a target (10% / 20% / 30%); governance rises by the mapped amount
 * (+5 / +10 / +20). Parchment art matches the season-close result modal (voting-modal assets).
 * No cancel/close — a target must be chosen to finalize the season.
 */
export const AllianceApsTargetModal: React.FC<AllianceApsTargetModalProps> = ({
  open,
  address,
  period,
  onBack,
  onFinalized,
}) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  if (!open || typeof document === 'undefined') return null

  const selectedGov = ALLIANCE_TASK_APS_TARGETS.find((x) => x.percent === selected)?.governance ?? null

  const handleConfirm = async () => {
    if (selected === null || busy) return
    setBusy(true)
    try {
      const res = await finalizeAllianceTaskPeriod(address, 'aps', period._id, { apsTargetPercent: selected })
      const delta = res.governanceDelta ?? 0
      alert.open(
        t('alliance.taskFinalized', {
          defaultValue: 'Official task locked. Governance {{delta}}.',
          delta: delta > 0 ? `+${delta}` : String(delta),
        }),
      )
      onFinalized()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      alert.open(e?.response?.data?.message || e?.message || t('alliance.taskFinalizeFailed', { defaultValue: 'Could not lock the task.' }))
      setBusy(false)
    }
  }

  return (
    <TaskModalShell
      title={t('alliance.apsTargetTitle', { defaultValue: 'Set APS Growth Target' })}
      subtitle={t('alliance.apsTargetSubtitle', { defaultValue: 'Commit to a growth target for this season.' })}
      padding="5% 5% 4%"
    >
      <div className="flex flex-col" style={{ gap: '3cqw' }}>
        {ALLIANCE_TASK_APS_TARGETS.map((tgt) => (
          <TaskOptionCard
            key={tgt.percent}
            selected={selected === tgt.percent}
            disabled={busy}
            onClick={() => setSelected(tgt.percent)}
            label={`${tgt.percent}%`}
            hint={t('alliance.apsTargetGov', { defaultValue: 'Governance +{{gov}}', gov: tgt.governance })}
          />
        ))}
      </div>

      <TaskFooter
        onBack={onBack}
        backLabel={t('common.back', { defaultValue: 'Back' })}
        backDisabled={busy}
        onConfirm={() => void handleConfirm()}
        confirmDisabled={selected === null || busy}
        confirmLabel={busy ? t('common.loading', { defaultValue: 'Loading...' }) : t('alliance.apsTargetConfirm', { defaultValue: 'Confirm Target' })}
      />
    </TaskModalShell>
  )
}

export default AllianceApsTargetModal
